import React, { useMemo, useState } from 'react';
import { Empty, Segmented, Button, Space, Popover } from 'antd';
import { CheckOutlined, DeleteOutlined, SettingOutlined } from '@ant-design/icons';
import { AnimatePresence } from 'framer-motion';
import type { Notification, NotificationType, NotificationGroup } from '../../types/notification.types';
import { useAppStore } from '../../stores/useAppStore';
import { NotificationItem } from './NotificationItem';
import { NotificationSettings } from './NotificationSettings';

type ViewMode = 'popover' | 'drawer' | 'page';

interface NotificationCenterProps {
  mode?: ViewMode;
  maxHeight?: number | string;
  showFilters?: boolean;
  showActions?: boolean;
  onClose?: () => void;
}

const groupNotificationsByDate = (notifications: Notification[]): NotificationGroup[] => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);
  
  const groups: NotificationGroup[] = [
    { label: 'Сегодня', notifications: [] },
    { label: 'Вчера', notifications: [] },
    { label: 'Ранее', notifications: [] },
  ];

  notifications.forEach((notification) => {
    const notifDate = new Date(notification.timestamp);
    notifDate.setHours(0, 0, 0, 0);
    
    if (notifDate.getTime() === today.getTime()) {
      groups[0].notifications.push(notification);
    } else if (notifDate.getTime() === yesterday.getTime()) {
      groups[1].notifications.push(notification);
    } else {
      groups[2].notifications.push(notification);
    }
  });

  return groups.filter((group) => group.notifications.length > 0);
};

export const NotificationCenter: React.FC<NotificationCenterProps> = ({
  mode = 'popover',
  maxHeight = mode === 'popover' ? 400 : '100%',
  showFilters = true,
  showActions = true,
  onClose,
}) => {
  const notifications = useAppStore((state) => state.notifications);
  const markAllAsRead = useAppStore((state) => state.markAllAsRead);
  const clearAll = useAppStore((state) => state.clearAll);
  const disabledTypes = useAppStore((state) => state.settings.disabledTypes);
  
  const [filterType, setFilterType] = useState<NotificationType | 'all'>('all');

  const filteredNotifications = useMemo(() => {
    let filtered = notifications.filter((n) => !disabledTypes.includes(n.type));
    
    if (filterType !== 'all') {
      filtered = filtered.filter((n) => n.type === filterType);
    }
    
    return filtered;
  }, [notifications, filterType, disabledTypes]);

  const groupedNotifications = useMemo(
    () => groupNotificationsByDate(filteredNotifications),
    [filteredNotifications]
  );

  const unreadCount = notifications.filter((n) => !n.read).length;
  const compact = mode === 'popover';

  const handleMarkAllAsRead = () => {
    markAllAsRead();
  };

  const handleClearAll = () => {
    clearAll();
  };

  return (
    <div className={`notification-center notification-center--${mode}`}>
      {showActions && (
        <div className="notification-center-header">
          <h3 className="notification-center-title">
            Уведомления {unreadCount > 0 && <span className="unread-badge">({unreadCount})</span>}
          </h3>
          
          <Space size="small">
            {unreadCount > 0 && (
              <Button
                type="text"
                size="small"
                icon={<CheckOutlined />}
                onClick={handleMarkAllAsRead}
              >
                Прочитать все
              </Button>
            )}
            {notifications.length > 0 && (
              <Button
                type="text"
                size="small"
                icon={<DeleteOutlined />}
                onClick={handleClearAll}
                danger
              >
                Очистить
              </Button>
            )}
            <Popover
              content={<NotificationSettings />}
              trigger="click"
              placement={mode === 'page' ? 'bottomRight' : 'bottom'}
              overlayClassName="notification-settings-popover"
            >
              <Button
                type="text"
                size="small"
                icon={<SettingOutlined />}
              >
                Настройки
              </Button>
            </Popover>
          </Space>
        </div>
      )}

      {showFilters && notifications.length > 0 && (
        <div className="notification-center-filters">
          <Segmented
            value={filterType}
            onChange={(value) => setFilterType(value as NotificationType | 'all')}
            options={[
              { label: 'Все', value: 'all' },
              { label: 'Данные', value: 'data-update' },
              { label: 'Система', value: 'system' },
              { label: 'Ошибки', value: 'error' },
            ]}
            size="small"
          />
        </div>
      )}

      <div
        className="notification-center-list"
        style={{ maxHeight, overflowY: 'auto' }}
      >
        {groupedNotifications.length === 0 ? (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description="Нет уведомлений"
            style={{ margin: '40px 0' }}
          />
        ) : (
          <AnimatePresence>
            {groupedNotifications.map((group) => (
              <div key={group.label} className="notification-group">
                <div className="notification-group-label">{group.label}</div>
                {group.notifications.map((notification) => (
                  <NotificationItem
                    key={notification.id}
                    notification={notification}
                    compact={compact}
                    onClose={onClose}
                  />
                ))}
              </div>
            ))}
          </AnimatePresence>
        )}
      </div>
    </div>
  );
};


